import React, { type ReactElement, useCallback, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { toInteger } from 'lodash';
import { FormProvider, type SubmitHandler, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import FormButtonsContainer from 'atom-components/form-buttons-container';
import CancelButton from 'atom-components/form-fields-for-hook-form/cancel-button';
import SubmitButton from 'atom-components/form-fields-for-hook-form/submit-button';
import { isAnActiveMarkerOfResponse } from 'pages/marking/helpers/marker.helpers';
import { markStatusDisplayName } from 'pages/marking/helpers/presentation.helpers';
import {
  markersOfResponseParams,
  marksOfResponseParams
} from 'pages/marking/helpers/request-params.helpers';
import { getMarkersOfResponse } from 'pages/marking/hooks/use-markers-of-response';
import type { ChangeMarkerMark } from 'pages/marking/types';
import type { BaseCustomerUser } from 'services/assist-api/entities/customer-user/types';
import {
  usePatchTestInstanceResponseMark,
  useTestInstanceResponseMarks
} from 'services/assist-api/entities/test-instance-response-mark/hooks';
import {
  MarkStatus,
  type TestInstanceResponseMarkWithActiveMarkerAndResponse
} from 'services/assist-api/entities/test-instance-response-mark/types';
import { usePostTestInstanceResponseMarker } from 'services/assist-api/entities/test-instance-response-marker/hooks';
import type { TestInstanceResponseMarkerWithUser } from 'services/assist-api/entities/test-instance-response-marker/types';
import { AssistQueryClient } from 'services/assist-api/query-client';
import ActiveMarker from './active-marker';
import {
  type ChangeActiveMarkerFormValues,
  changeActiveMarkerFormSchema
} from './change-active-marker-form.schema';
import ConfirmMarkerChangeModal from './confirm-marker-change-modal';

type Props = {
  mark: TestInstanceResponseMarkWithActiveMarkerAndResponse;
  markers: TestInstanceResponseMarkerWithUser[];
  organisationUsers: BaseCustomerUser[];
  onClose: () => void;
};

export default function ChangeActiveMarkerForm({
  mark,
  markers,
  organisationUsers,
  onClose
}: Props): ReactElement {
  const [intendedMarkerId, setIntendedMarkerId] = useState<number | undefined>(undefined);
  const [isConfirmMarkerChangeModalOpen, setIsConfirmMarkerChangeModalOpen] =
    useState<boolean>(false);

  const activeOrganisationUserId =
    markers.find((m) => m.id === mark.activeMarker?.id)?.user.id || undefined;

  const methods = useForm({
    mode: 'onTouched',
    resolver: zodResolver(
      changeActiveMarkerFormSchema({ organisationUserId: activeOrganisationUserId })
    ),
    defaultValues: { organisationUserId: activeOrganisationUserId }
  });
  const { handleSubmit, formState } = methods;

  // Fetch marks and mark items linked to the response to determine if the intended marker has
  // marked anything.
  const marksQuery = useTestInstanceResponseMarks<ChangeMarkerMark>(
    marksOfResponseParams(mark.testInstanceResponse.id),
    { enabled: true }
  );

  const displayUpdatedMarkMessage = useCallback(
    async (newStatus: MarkStatus, newActiveMarkerId: number) => {
      // Refetch the markers of the response as the marker could have been allocated just prior to
      // saving.
      // Note: we should only clear the markers cache for the "markers of response" params, as there
      // is another test-instance-response-markers query used to populate the Marks for Review table
      // behind the modal. Clearing the cache for all test-instance-response-markers queries causes
      // the Change Active Marker modal to close prematurely.
      await AssistQueryClient.invalidateQueries([
        'test-instance-response-markers',
        markersOfResponseParams(mark.testInstanceResponse.id)
      ]);
      const markersQueryResponse = await getMarkersOfResponse(mark.testInstanceResponse.id);
      const refreshedMarkers: TestInstanceResponseMarkerWithUser[] =
        markersQueryResponse?.data.json;

      // Get the user associated with the new active marker:
      const marker = refreshedMarkers.find((mkr) => mkr.id === newActiveMarkerId);
      const organisationUser = organisationUsers.find((ou) => ou.id === marker?.user.id);

      // The user firstname and lastname should be based on marker data from the
      // backend, rather than the organisation user associated with the form. This
      // ensures that the active marker was correctly updated on the backend.
      toast.success(
        `Updated mark with status ${markStatusDisplayName(newStatus)} and new active marker: ${organisationUser?.user.firstname} ${organisationUser?.user.lastname}`
      );

      // Ensure that the marking task and mark caches are cleared as well so that we see the new
      // active marker on the Marker Dashboard and Mark Response form straight away.
      // Invalidating these queries will also close the Change Active Marker modal since the
      // Marks for Review table will be re-rendered. Therefore, we don't need to close the modal
      // explictly.
      await AssistQueryClient.invalidateQueries(['test-instance-response-markers/marking-tasks']);
      await AssistQueryClient.invalidateQueries(['test-instance-response-mark']);
    },
    [mark.testInstanceResponse.id, organisationUsers]
  );

  const patchTestInstanceResponseMark = usePatchTestInstanceResponseMark({
    onSuccess: ({ data }) => {
      displayUpdatedMarkMessage(data.json.status, data.json.activeMarker.id);
    },
    onError: (error) => {
      toast.error(`Failed to update mark with new active marker: ${error.message}`);
    }
  });

  /**
   * Reassigns the mark to the new active marker.
   */
  const updateMarkWithNewActiveMarker = useCallback(
    (newActiveMarkerId: number) => {
      patchTestInstanceResponseMark.mutate({
        id: mark.id,
        data: {
          activeMarkerId: newActiveMarkerId,
          status: MarkStatus.Reassigned,

          // Prevents new marker from unintentionally sending mark back into review:
          needsAttention: false
        }
      });
    },
    [mark.id, patchTestInstanceResponseMark]
  );

  /**
   * Check if that marker already has any marks or mark items associated with the response and warn
   * the marking admin before changing, otherwise proceed with setting the new active marker.
   */
  const warnOrUpdateMarkWithNewActiveMarker = useCallback(
    (markerId: number) => {
      if (isAnActiveMarkerOfResponse(markerId, marksQuery.data?.json || [])) {
        // Set the intended marker to change to. We'll use this to populate the confirm marker
        // change modal.
        setIntendedMarkerId(markerId);
        setIsConfirmMarkerChangeModalOpen(true);
      } else {
        updateMarkWithNewActiveMarker(markerId);
      }
    },
    [marksQuery.data?.json, updateMarkWithNewActiveMarker]
  );

  const postTestInstanceResponseMarker = usePostTestInstanceResponseMarker({
    onSuccess: ({ data }) => {
      toast.success("The selected marker wasn't previously allocated to the response, but now is.");
      warnOrUpdateMarkWithNewActiveMarker(data.json.id);
    },
    onError: (error) => {
      toast.error(`Failed to allocate response to marker: ${error.message}`);
    }
  });

  /**
   * Check if the selected marker is already a marker of the response. If not,
   * create a marker record first, then updates the mark with the new active
   * marker.
   */
  const attemptToSetNewActiveMarker = useCallback(
    async (newActiveOrganisationUserId: number) => {
      // Refetch the markers of the response in case further allocations have occurred between
      // opening the modal and saving:
      //
      // Note: we should only clear the markers cache for the "markers of response" params, as there
      // is another test-instance-response-markers query used to populate the Marks for Review table
      // behind the modal. Clearing the cache for all test-instance-response-markers queries causes
      // the Change Active Marker modal to close prematurely.
      await AssistQueryClient.invalidateQueries([
        'test-instance-response-markers',
        markersOfResponseParams(mark.testInstanceResponse.id)
      ]);
      const markersQueryResponse = await getMarkersOfResponse(mark.testInstanceResponse.id);
      const refreshedMarkers: TestInstanceResponseMarkerWithUser[] =
        markersQueryResponse?.data.json;

      const marker = refreshedMarkers.find(
        (mkr) =>
          mkr.user.id === newActiveOrganisationUserId &&
          mkr.testInstanceResponse.id === mark.testInstanceResponse.id
      );
      if (marker) {
        warnOrUpdateMarkWithNewActiveMarker(marker.id);
      } else {
        postTestInstanceResponseMarker.mutate({
          testInstanceResponseId: mark.testInstanceResponse.id,
          customerUserId: newActiveOrganisationUserId
        });
      }
    },
    [
      mark.testInstanceResponse.id,
      postTestInstanceResponseMarker,
      warnOrUpdateMarkWithNewActiveMarker
    ]
  );

  const onSubmit: SubmitHandler<ChangeActiveMarkerFormValues> = (data) => {
    attemptToSetNewActiveMarker(data.organisationUserId || 0);
  };

  const { isSubmitting } = formState;

  const submittingOrSaving =
    isSubmitting ||
    postTestInstanceResponseMarker.isLoading ||
    patchTestInstanceResponseMark.isLoading;

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <ConfirmMarkerChangeModal
          isOpen={isConfirmMarkerChangeModalOpen}
          onClose={(): void => {
            setIntendedMarkerId(undefined);
            setIsConfirmMarkerChangeModalOpen(false);
          }}
          onConfirm={(markerId: number): void => {
            updateMarkWithNewActiveMarker(markerId);
            setIntendedMarkerId(undefined);
            setIsConfirmMarkerChangeModalOpen(false);
          }}
          organisationUsers={organisationUsers}
          // Intended marker ID will be set at this point, so we can safely convert it to an
          // integer:
          intendedMarkerId={toInteger(intendedMarkerId)}
          marksOfResponse={marksQuery.data?.json || []}
        />
        <p>
          You are changing the active marker of <b>mark #{mark.id}</b> for assessment{' '}
          <b>response #{mark.testInstanceResponse.id}</b>.
        </p>
        <p>
          Select a new active marker from the list. There can only be one active marker per mark at
          a time. Previously scored items within the mark will continue to be associated with the
          previous marker unless they are rescored.
        </p>
        <ActiveMarker organisationUsers={organisationUsers} />
        <FormButtonsContainer>
          <CancelButton onClick={onClose} />
          <SubmitButton disabled={submittingOrSaving}>
            {submittingOrSaving ? 'Attempting Change...' : 'Change Active Marker'}
          </SubmitButton>
        </FormButtonsContainer>
      </form>
    </FormProvider>
  );
}
